<script setup lang="ts">
import { PageName } from '@/api/tracking';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import Divider from '@/components/common/divider.vue';
import SurveyContentRenderer from '@/components/survey/generate-content/survey-content-renderer.vue';
import { FeedSummaryResponse, SummarizableFeature } from '@/types';
import { useFeedSummaryActions } from './composables/use-feed-summary-actions';
import { useFeedSummaryCollapse } from './composables/use-feed-summary-collapse';
import { useFeedSummaryContent } from './composables/use-feed-summary-content';
import FeedSummaryFeedback from './feed-summary-feedback.vue';

const props = defineProps<{
  feedSummary: FeedSummaryResponse;
  feature: SummarizableFeature;
  pageName: PageName;
  themeId?: number;
  suffix?: string;
}>();

const { summaryFeature, contentParts, status, summaryInfo } =
  useFeedSummaryContent(props);

const {
  isClose,
  hasOpened,
  displayIsInProgress,
  progressBarWidth,
  toggleClose,
  closeTransitionEnd,
} = useFeedSummaryCollapse(props, status, summaryInfo);

const { hasConvertedSurvey, convertToSummary, copySummary } =
  useFeedSummaryActions(props, summaryFeature, summaryInfo);
</script>
<template>
  <div class="feed-summary">
    <div class="title">
      <button class="toggle-button c-title-bold--xm" @click="toggleClose">
        主要トピック {{ suffix ?? '' }}
        <DgrIcon
          v-if="!status.isInProgress"
          name="chevron-up"
          size="small"
          class="toggle-icon"
          :class="{ close: isClose }"
        />
      </button>
    </div>
    <div
      class="container"
      :class="{ close: isClose, 'has-opened': hasOpened }"
      @transitionend="closeTransitionEnd"
    >
      <div class="inner-container">
        <div v-if="status.isInProgress" class="loading-container">
          <div class="loading-header">
            <DgrLoading size="small" />
            <span class="c-text-regular--m loading-text"
              >主要トピックを作成しています</span
            >
          </div>
          <div class="progress-bar">
            <div class="progress-bar-fill" />
          </div>
          <span class="c-text-regular--s loading-description"
            >通常5〜10秒程度かかります</span
          >
        </div>
        <div class="summary-container" :class="{ close: displayIsInProgress }">
          <div class="summary-inner">
            <div class="summary">
              <SurveyContentRenderer
                :content-parts
                :status
                :enable-animation="false"
                :feature
                :page-name
                :summary-info="summaryInfo"
              />
              <FeedSummaryFeedback
                :summary-info="summaryInfo"
                :feature="summaryFeature"
                :page-name
              />
            </div>
            <Divider />
            <button
              class="action c-title-bold--m c-btn c-btn--auto"
              @click="copySummary"
            >
              <DgrIcon name="copy" size="small" />
              コピー
            </button>
            <button
              class="action c-title-bold--m c-btn c-btn--auto"
              @click="convertToSummary"
            >
              <DgrIcon
                :name="hasConvertedSurvey ? 'thread' : 'plus'"
                size="small"
              />
              {{
                hasConvertedSurvey
                  ? 'スレッドを確認'
                  : 'スレッドを作成して追加質問'
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.feed-summary {
  border: 1px solid $color-gray400;
  border-radius: 4px;
}

.title {
  padding: 16px;
}

.toggle-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  padding: 4px;
  width: 100%;
  height: auto;
  cursor: pointer;
}

.toggle-icon {
  transition: transform 0.3s;
  &.close {
    transform: rotate(-180deg);
  }
}

.container {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.3s ease;

  /* 開いているときにoverflow: hiddenが残っていると
     マークのポップアップが見切れるので、開いていないときだけ
     overflow: hiddenを設定するようにする */
  &:not(.has-opened) {
    .inner-container {
      overflow: hidden;
    }
  }
  &.close {
    grid-template-rows: 0fr;
  }
}

.loading {
  margin-left: 16px;
}

.loading-container {
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.loading-header {
  display: flex;
  align-items: center;
  gap: 4px;
}

.loading-text {
  color: $color-gray1000;
}

.loading-description {
  color: $color-gray800;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: $color-gray400;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  width: v-bind(progressBarWidth);
  height: 100%;
  background: $color-green600;
  border-radius: 4px;
  transition: width 0.1s linear;
}

.summary-container {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.5s ease;

  .summary-inner {
    overflow: hidden;
  }
  &.close {
    grid-template-rows: 0fr;
    padding-bottom: 16px;
  }
}

.summary {
  padding: 0 16px 16px;
}

.action {
  background: $color-white;
  border: none;
  justify-content: flex-start;

  gap: 4px;
  height: 40px;
  margin: 16px;

  &:hover {
    background: $color-gray200;
  }
}

.source-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;

  > * {
    box-sizing: border-box;
  }
}
</style>
